import { useEffect, useState } from 'react';
import { Button, Tabs, TextField, Autocomplete } from '../../tailwindcomponents';
import { DatePicker } from '../../MaterialUiComponents';
import { Typography } from '../../tailwindcomponents';
import Overview from './Overview';
import NewWorldTrip from './NewWorldTrip';
import WorldToolsApi from './../../api/worldTools'
import { destinations, WorldTrip } from '../../types/worldTools';
import { RootState } from '../../reducer';
import { useSelector } from 'react-redux';
import EditDestinations from './EditDestinations';
import taskApi from './../../api/tasks';
import { useQueryTab } from '../../utils/usePersistedTab';

// Main World Tools Page. Fetches all the World Trips. Contains 3 tabs, Overview page, create/edit page and dictionary edit page
function WorldTools() {
    const store = useSelector((state: RootState) => state);
    const isWorldToolAdmin = store.user.me.user_clearence_groups.includes("World Tools Edit");
    const { tabValue, setTabValue } = useQueryTab("tab", 0);
    const [selectedTrip, setSelectedTrip] = useState<WorldTrip>();
    const [allWorldTrips, setAllWorldTrips] = useState<WorldTrip[]>();
    const [allDestinations, setAllDestinations] = useState<destinations[]>([]);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [destination, setDestination] = useState<destinations | null>(null);
    const [locationId, setLocationId] = useState<number>(-1);
    const [status, setStatus] = useState<string | null>(null);
    const [tailNumber, setTailNumber] = useState<string | null>(null);
    const statusOptions = ["In Work", "Not Started", "Tools missing", "Complete"];
    const [tails, setTails] = useState<string[]>([]);

    useEffect(() => {
        refresh();
        if (allDestinations.length === 0) {
            WorldToolsApi.GetAllWorldTripLocations().then(res => {
                setAllDestinations(res.data.filter(destination => destination.visible === true));
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, locationId, status, tailNumber]);

    useEffect(() => {
        taskApi.GetAllTailNumbers().then(res => {
            setTails(res.data);
        });
    }, []);

    const refresh = () => {
        WorldToolsApi.GetAllWorldTrips(startDate, endDate, locationId, status, tailNumber).then(res => {
            setAllWorldTrips(res.data);
        });
    }

    const changeTab = (tabValue: number) => {
        if (tabValue === 0) {
            onResetFields();
            refresh();
        }
        setSelectedTrip(undefined);
        setTabValue(tabValue);
    }

    const openTrip = (index: number) => {
        if (allWorldTrips !== undefined) {
            setSelectedTrip(allWorldTrips[index]);
        }
    }
    const closeForm = () => {
        refresh();
        setSelectedTrip(undefined);
        changeTab(0);
    }
    const setSelectedLocation = (location: destinations | null) => {
        if (location !== null) {
            setDestination(location);
            setLocationId(location.id);
        }
        else {
            setDestination(null);
            setLocationId(-1);
        }
    }
    const onResetFields = () => {
        setStartDate(null);
        setEndDate(null);
        setDestination(null);
        setLocationId(-1);
        setStatus(null);
        setTailNumber(null);
    }

    return (
        <div className="w-full">
            <Tabs
                tabNames={isWorldToolAdmin ? ["Overview", "Create new world trip", "Edit Destinations"] : ["Overview"]}
                onChangeTabValue={(tabValue: number) => changeTab(tabValue)}
                tabValue={tabValue}
            />
            {tabValue === 0 && allWorldTrips !== undefined && selectedTrip === undefined &&
                <div className="w-full">
                    <div className="flex flex-wrap">
                        <div className="flex flex-wrap w-full sm:w-5/12 lg:w-1/6">
                            <DatePicker type="Date" selected={startDate} label="Start date from" onDateChange={(date: Date) => setStartDate(date)} />
                        </div>
                        <div className="flex flex-wrap w-full sm:w-5/12 lg:w-1/6">
                            <DatePicker type="Date" selected={endDate} label="Start date to" onDateChange={(date: Date) => setEndDate(date)} />
                        </div>
                        <div className="flex flex-wrap w-full sm:w-5/12 lg:w-1/6" >
                            <Autocomplete
                                fullWidth
                                className="mr-8"
                                noOptionsText="No tails found"
                                options={allDestinations}
                                getOptionLabel={option => option.location}
                                defaultValue={destination}
                                value={destination}
                                clearOnEscape
                                autoHighlight
                                onChange={(e: any, newValue: destinations | string | null) => typeof newValue !== 'string' ? setSelectedLocation(newValue) : {}}
                                renderInput={(params) => (
                                    <TextField {...params} label="Destination" variant="standard" />
                                )}
                            />
                        </div>
                        <div className="flex flex-wrap w-full sm:w-5/12 lg:w-1/6" >
                            <Autocomplete
                                fullWidth
                                className="mr-8"
                                noOptionsText="No status found"
                                options={statusOptions}
                                value={status}
                                clearOnEscape
                                autoHighlight
                                onChange={(e: any, newStatus: any | null) => setStatus(newStatus)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Status" variant="standard" />
                                )}
                            />
                        </div>
                        <div className="flex flex-wrap w-full sm:w-5/12 lg:w-1/6" >
                            <Autocomplete
                                fullWidth
                                className="mr-8"
                                options={tails}
                                value={tailNumber}
                                noOptionsText="No tails found"
                                clearOnEscape
                                autoHighlight
                                onChange={(e: any, newValue: string | null) => setTailNumber(newValue)}
                                renderInput={(params) => (
                                    <TextField {...params} label="Tails" margin="none" variant="standard" size="small" fullWidth />
                                )}
                            />
                        </div>
                        <div className="flex flex-wrap w-full sm:w-5/12 lg:w-1/6" >
                            <Button variant="text" className="h-[var(--ds-control-h)] mt-4" color="primary" size="small" onClick={() => onResetFields()}>Reset filters</Button>
                        </div>
                        <div className="w-full mt-2 text-gray-500">
                            <Typography text={allWorldTrips !== undefined ? allWorldTrips.length + " reports found" : '0 reports found'} />
                        </div>
                    </div>
                    <Overview userId={store.user.me.user_id} allWorldTrips={allWorldTrips} refresh={() => refresh()} openTrip={(index: number) => openTrip(index)} />
                </div>
            }
            {(tabValue === 1 || selectedTrip !== undefined) &&
                <div className="w-full">
                    <NewWorldTrip allDestinations={allDestinations} selected={selectedTrip} closeForm={() => closeForm()} />
                </div>
            }
            {tabValue === 2 &&
                <div className="w-full">
                    <EditDestinations />
                </div>
            }
        </div>
    );
}

export default WorldTools;